import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Music,
  ArrowLeft,
  Calendar,
  FileText,
  Users,
  Globe,
  Lock,
  Award,
  Download,
  Upload,
  Loader2,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Edit,
  Trash2,
  Eye
} from 'lucide-react';
import { worksAPI } from '../services/api';
import { toast } from 'sonner';

const WorkDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [work, setWork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingCertificate, setGeneratingCertificate] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [downloadingPOA, setDownloadingPOA] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [audioFile, setAudioFile] = useState(null);

  useEffect(() => {
    loadWork();
  }, [id]);

  const loadWork = async () => {
    try {
      setLoading(true);
      const response = await worksAPI.get(id);
      setWork(response.data);
    } catch (error) {
      console.error('Error loading work:', error);
      toast.error('Erro ao carregar obra');
      navigate('/works');
    } finally {
      setLoading(false);
    }
  };

  const handleAudioFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tipo de arquivo
      const validTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/m4a', 'audio/flac'];
      if (!validTypes.includes(file.type) && !file.name.match(/\.(mp3|wav|ogg|m4a|flac)$/i)) {
        toast.error('Formato de áudio inválido. Use MP3, WAV, OGG, M4A ou FLAC');
        return;
      }
      
      // Validar tamanho (máximo 50MB)
      if (file.size > 50 * 1024 * 1024) {
        toast.error('Arquivo muito grande. Tamanho máximo: 50MB');
        return;
      }
      
      setAudioFile(file);
      toast.success(`Arquivo selecionado: ${file.name}`);
    }
  };

  const handleUploadAudio = async () => {
    if (!audioFile) {
      toast.error('Selecione um arquivo de áudio primeiro');
      return;
    }

    try {
      setUploadingAudio(true);
      const formData = new FormData();
      formData.append('audio', audioFile);
      
      await worksAPI.uploadAudio(id, formData);
      
      toast.success('Áudio enviado com sucesso! 🎵', {
        description: 'A capa será gerada automaticamente.'
      });
      
      setAudioFile(null);
      await loadWork(); // Recarregar obra para mostrar o áudio
    } catch (error) {
      console.error('Error uploading audio:', error);
      const errorMessage = error.response?.data?.detail || 'Erro ao enviar áudio';
      toast.error(errorMessage);
    } finally {
      setUploadingAudio(false);
    }
  };

  const handleGenerateCertificate = async () => {
    try {
      setGeneratingCertificate(true);
      await worksAPI.generateCertificate(id);
      toast.success('Certificado gerado com sucesso!');
      await loadWork(); // Reload to get updated certificate info
    } catch (error) {
      console.error('Error generating certificate:', error);
      
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Erro ao gerar certificado';
      
      if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(err => err.msg).join(', ');
      } else if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      }
      
      toast.error(errorMessage);
    } finally {
      setGeneratingCertificate(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      setDownloadingPDF(true);
      console.log('🔵 Baixando certificado...');
      
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Token não encontrado. Faça login novamente.');
        return;
      }
      
      // Baixar PDF diretamente do endpoint (evita bloqueadores)
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/works/${id}/certificate/pdf`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Erro na resposta:', errorText);
        throw new Error('Erro ao baixar certificado');
      }

      // Converter resposta em blob
      const blob = await response.blob();
      console.log('🔵 PDF recebido, tamanho:', blob.size, 'bytes');
      
      // Criar URL blob local
      const blobUrl = URL.createObjectURL(blob);
      
      // Criar link temporário para download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `certificado-${work.title.replace(/[^a-z0-9]/gi, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Limpar URL blob após 1 segundo
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      
      toast.success('Certificado baixado com sucesso!');
      console.log('✅ Download concluído');
      
    } catch (error) {
      console.error('❌ ERRO ao baixar certificado:', error);
      toast.error(`Erro ao baixar certificado: ${error.message}`);
    } finally {
      setDownloadingPDF(false);
    }
  };

  const handleDownloadPowerOfAttorney = async () => {
    try {
      setDownloadingPOA(true);
      console.log('🔵 Baixando procuração...');
      
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Token não encontrado. Faça login novamente.');
        return;
      }
      
      // Baixar PDF diretamente do endpoint (evita bloqueadores)
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/works/${id}/power-of-attorney/pdf`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        if (response.status === 400) {
          toast.error('Procuração não foi solicitada para esta obra');
          return;
        } else {
          const errorText = await response.text();
          console.error('Erro na resposta:', errorText);
          throw new Error('Erro ao baixar procuração');
        }
      }

      // Converter resposta em blob
      const blob = await response.blob();
      console.log('🔵 PDF recebido, tamanho:', blob.size, 'bytes');
      
      // Criar URL blob local
      const blobUrl = URL.createObjectURL(blob);
      
      // Criar link temporário para download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `procuracao-${work.title.replace(/[^a-z0-9]/gi, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Limpar URL blob após 1 segundo
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      
      toast.success('Procuração baixada com sucesso!');
      console.log('✅ Download concluído');
      
    } catch (error) {
      console.error('❌ ERRO ao gerar procuração:', error);
      toast.error(`Erro ao gerar procuração: ${error.message}`);
    } finally {
      setDownloadingPOA(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Tem certeza que deseja excluir esta obra? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      await worksAPI.delete(id);
      toast.success('Obra excluída com sucesso!');
      navigate('/works');
    } catch (error) {
      console.error('Error deleting work:', error);
      toast.error('Erro ao excluir obra');
    }
  };

  const handleCopyPublicLink = async () => {
    const publicUrl = `${window.location.origin}/obra/${id}`;
    
    try {
      // Tenta usar a API moderna de Clipboard
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(publicUrl);
        toast.success('Link público copiado!');
        return;
      }
      
      // Fallback: método antigo usando textarea temporário
      const textArea = document.createElement('textarea');
      textArea.value = publicUrl;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        const successful = document.execCommand('copy');
        textArea.remove();
        if (successful) {
          toast.success('Link público copiado!');
        } else {
          throw new Error('Copy command failed');
        }
      } catch (err) {
        textArea.remove();
        throw err;
      }
    } catch (err) {
      console.error('Erro ao copiar:', err);
      toast.error('Não foi possível copiar. Link: ' + publicUrl);
    }
  };

  const getWorkTypeLabel = (type) => {
    const types = {
      letra: 'Letra',
      musica: 'Música',
      partitura: 'Partitura',
      letra_e_musica: 'Letra e Música',
      arranjo: 'Arranjo',
      producao: 'Produção',
      video: 'Vídeo'
    };
    return types[type] || type;
  };

  const getStatusInfo = (status) => {
    const statusInfo = {
      draft: {
        color: 'text-gray-800 bg-gray-100',
        icon: AlertCircle,
        label: 'Rascunho'
      },
      registered: {
        color: 'text-blue-800 bg-blue-100',
        icon: CheckCircle,
        label: 'Registrado'
      },
      verified: {
        color: 'text-green-800 bg-green-100',
        icon: CheckCircle,
        label: 'Verificado'
      }
    };
    return statusInfo[status] || statusInfo.draft;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Carregando detalhes...</p>
        </div>
      </div>
    );
  }

  if (!work) return null;

  const statusInfo = getStatusInfo(work.status);
  const StatusIcon = statusInfo.icon;
  const hasCertificate = work.certificate_hash;
  const hasFiles = work.files && work.files.length > 0;
  const hasAudio = work.audio_file_id;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-8">
      <div className="max-w-5xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Voltar
        </button>

        {/* Alerta sobre Bloqueador de Anúncios */}
        {hasCertificate && (
          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-6 rounded-r-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-yellow-600 mt-1 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-semibold text-yellow-800 mb-1">
                  ⚠️ Problema com Downloads?
                </p>
                <p className="text-sm text-yellow-700">
                  Se os downloads de <strong>certificados, procuração, arquivos ou áudios não funcionarem</strong>, 
                  por favor <strong>desative seu bloqueador de anúncios</strong> (AdBlock, uBlock Origin, etc.) para este site. 
                  Os bloqueadores podem interferir com o download de PDFs e arquivos.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Public Indexing Alert */}
        {work.visibility === 'public' && hasCertificate && (
          <div className="bg-gradient-to-r from-green-50 to-blue-50 border-l-4 border-green-500 p-4 mb-6 rounded-r-lg">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <Globe className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-semibold text-green-800 mb-1">
                    🌍 Obra Publicada e Indexada Publicamente
                  </p>
                  <p className="text-sm text-green-700 mb-2">
                    Esta obra está disponível para indexação pelo Google, Bing e plataformas de IA (GPT, etc). 
                    Trechos da letra podem aparecer nos resultados de busca, identificando automaticamente sua autoria e anterioridade.
                  </p>
                  <button
                    onClick={handleCopyPublicLink}
                    className="text-sm bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-all"
                  >
                    📋 Copiar Link Público
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="bg-white rounded-xl p-8 shadow-lg mb-6">
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center gap-3">
              <Music className="w-12 h-12 text-blue-600" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{work.title}</h1>
                <p className="text-gray-500">{getWorkTypeLabel(work.work_type)}</p>
              </div>
            </div>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${statusInfo.color}`}>
              <StatusIcon className="w-5 h-5" />
              <span className="font-medium">{statusInfo.label}</span>
            </div>
          </div>

          {work.description && (
            <p className="text-gray-600 mb-6">{work.description}</p>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-6 border-t">
            <button
              onClick={() => navigate(`/works/${id}/edit`)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-all"
            >
              <Edit className="w-4 h-4" />
              Editar
            </button>
            <button
              onClick={handleDelete}
              className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-all"
            >
              <Trash2 className="w-4 h-4" />
              Excluir
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Informações Básicas</h2>
              <div className="space-y-3">
                {work.genre && (
                  <div className="flex items-center gap-3">
                    <Music className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Gênero</p>
                      <p className="font-medium">{work.genre}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Data de Registro</p>
                    <p className="font-medium">
                      {new Date(work.timestamp).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {work.visibility === 'public' ? (
                    <Globe className="w-5 h-5 text-gray-400" />
                  ) : (
                    <Lock className="w-5 h-5 text-gray-400" />
                  )}
                  <div>
                    <p className="text-sm text-gray-500">Visibilidade</p>
                    <p className="font-medium">
                      {work.visibility === 'public' ? 'Pública' : 'Privada'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Coauthors */}
            {work.coauthors && work.coauthors.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Users className="w-6 h-6" />
                  Coautores
                </h2>
                <div className="space-y-3">
                  {work.coauthors.map((coauthor, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">{coauthor.name}</p>
                        <p className="text-sm text-gray-500">{coauthor.role}</p>
                      </div>
                      <span className="text-lg font-bold text-blue-600">
                        {coauthor.percentage}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* AI Declaration - Conformidade ECAD 2025 */}
            {work.ai_usage && (
              <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6 shadow-lg border-2 border-purple-200">
                <div className="flex items-start gap-3 mb-4">
                  <div className="bg-purple-100 p-3 rounded-lg">
                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-gray-900 mb-1">Declaração de Uso de IA</h2>
                    <p className="text-sm text-gray-600">Conformidade ECAD 2025 e Lei 9.610/98</p>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                    work.ai_usage_type === 'partial' ? 'bg-yellow-200 text-yellow-800' : 'bg-orange-200 text-orange-800'
                  }`}>
                    {work.ai_usage_type === 'partial' ? 'IA PARCIAL' : 'IA TOTAL'}
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Partes que usaram IA */}
                  {work.ai_parts_used && work.ai_parts_used.length > 0 && (
                    <div className="bg-white rounded-lg p-4 border border-purple-200">
                      <p className="text-sm font-semibold text-gray-700 mb-2">Partes com IA:</p>
                      <div className="flex flex-wrap gap-2">
                        {work.ai_parts_used.map((part, index) => {
                          const partLabels = {
                            'letra': '📝 Letra',
                            'melodia': '🎵 Melodia',
                            'harmonia': '🎹 Harmonia',
                            'arranjo': '🎼 Arranjo Musical',
                            'partitura': '🎼 Partitura',
                            'audio_voz': '🎤 Áudio/Voz',
                            'instrumental': '🎸 Instrumental',
                            'fonograma_completo': '💿 Fonograma Completo',
                            'outro': '➕ Outro'
                          };
                          return (
                            <span key={index} className="px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-full">
                              {partLabels[part] || part}
                            </span>
                          );
                        })}
                      </div>
                      {work.ai_other_specification && (
                        <p className="text-sm text-gray-600 mt-2">
                          <strong>Especificação:</strong> {work.ai_other_specification}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Ferramentas usadas */}
                  {work.ai_tools_used && (
                    <div className="bg-white rounded-lg p-4 border border-purple-200">
                      <p className="text-sm font-semibold text-gray-700 mb-1">Ferramentas Utilizadas:</p>
                      <p className="text-gray-900">{work.ai_tools_used}</p>
                    </div>
                  )}

                  {/* Prompt usado */}
                  {work.ai_prompt_used && (
                    <div className="bg-white rounded-lg p-4 border border-purple-200">
                      <p className="text-sm font-semibold text-gray-700 mb-1">Prompt/Comando Utilizado:</p>
                      <p className="text-gray-700 italic">"{work.ai_prompt_used}"</p>
                    </div>
                  )}

                  {/* Data da declaração */}
                  {work.ai_declaration_date && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-green-800">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-sm font-medium">
                          Declaração aceita em: {new Date(work.ai_declaration_date).toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {work.ai_usage === false && (
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 shadow-lg border-2 border-green-200">
                <div className="flex items-center gap-3">
                  <div className="bg-green-100 p-3 rounded-lg">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-green-900">Obra 100% Humana</h3>
                    <p className="text-sm text-green-700">Esta obra foi declarada como criada totalmente por humanos, sem uso de IA.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Upload Audio Section */}
            {!hasAudio && (
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 shadow-lg border-2 border-purple-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-purple-500 rounded-lg">
                    <Music className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Adicionar Áudio</h2>
                    <p className="text-sm text-gray-600">Upload de arquivo MP3, WAV, OGG, M4A ou FLAC</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* File Input */}
                  <div className="relative">
                    <input
                      type="file"
                      accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/m4a,audio/flac,.mp3,.wav,.ogg,.m4a,.flac"
                      onChange={handleAudioFileChange}
                      className="hidden"
                      id="audio-upload"
                      disabled={uploadingAudio}
                    />
                    <label
                      htmlFor="audio-upload"
                      className={`flex items-center justify-center gap-3 p-4 border-2 border-dashed rounded-lg cursor-pointer transition-all ${
                        audioFile 
                          ? 'border-purple-500 bg-purple-50' 
                          : 'border-gray-300 bg-white hover:border-purple-400 hover:bg-purple-50'
                      } ${uploadingAudio ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <Upload className="w-6 h-6 text-purple-600" />
                      <div className="text-center">
                        {audioFile ? (
                          <>
                            <p className="font-medium text-purple-900">{audioFile.name}</p>
                            <p className="text-sm text-purple-700">
                              {(audioFile.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="font-medium text-gray-700">Clique para selecionar o áudio</p>
                            <p className="text-sm text-gray-500">MP3, WAV, OGG, M4A ou FLAC (máx. 50MB)</p>
                          </>
                        )}
                      </div>
                    </label>
                  </div>

                  {/* Upload Button */}
                  {audioFile && (
                    <button
                      onClick={handleUploadAudio}
                      disabled={uploadingAudio}
                      className="w-full flex items-center justify-center gap-2 bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {uploadingAudio ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Enviando áudio...
                        </>
                      ) : (
                        <>
                          <Upload className="w-5 h-5" />
                          Enviar Áudio
                        </>
                      )}
                    </button>
                  )}

                  {/* Info */}
                  <div className="bg-purple-100 border border-purple-300 rounded-lg p-3">
                    <div className="flex gap-2">
                      <AlertCircle className="w-5 h-5 text-purple-700 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-purple-900">
                        <p className="font-medium mb-1">💡 Dica:</p>
                        <p>Após adicionar o áudio, você poderá tornar sua obra pública na galeria e ela estará disponível para ser ouvida por todos!</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Files and Audio */}
            {(hasFiles || hasAudio) && (
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <FileText className="w-6 h-6" />
                  Arquivos ({(work.files?.length || 0) + (hasAudio ? 1 : 0)})
                </h2>
                <div className="space-y-2">
                  {/* Audio File */}
                  {hasAudio && (
                    <div className="flex justify-between items-center p-3 bg-blue-50 border-2 border-blue-200 rounded-lg hover:bg-blue-100 transition-colors">
                      <div className="flex items-center gap-3">
                        <Music className="w-5 h-5 text-blue-600" />
                        <div>
                          <p className="font-medium text-blue-900">🎵 Áudio da Música (MP3)</p>
                          <p className="text-sm text-blue-700">
                            áudio • Arquivo anexado
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={async () => {
                            try {
                              console.log('🔵 Iniciando download de áudio...');
                              const token = localStorage.getItem('token');
                              
                              const response = await fetch(
                                `${process.env.REACT_APP_BACKEND_URL}/api/works/${work.id}/audio/download`,
                                {
                                  method: 'GET',
                                  headers: {
                                    'Authorization': `Bearer ${token}`
                                  }
                                }
                              );
                              
                              if (!response.ok) {
                                throw new Error(`Erro ${response.status}: ${response.statusText}`);
                              }
                              
                              console.log('🔵 Áudio recebido, criando blob...');
                              const blob = await response.blob();
                              console.log('🔵 Blob criado, tamanho:', blob.size, 'bytes');
                              
                              const blobUrl = URL.createObjectURL(blob);
                              
                              const a = document.createElement('a');
                              a.href = blobUrl;
                              a.download = `${work.title.replace(/[^a-z0-9]/gi, '_')}.mp3`;
                              document.body.appendChild(a);
                              a.click();
                              document.body.removeChild(a);
                              
                              setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
                              
                              toast.success('Áudio baixado com sucesso!');
                              console.log('✅ Download de áudio concluído');
                            } catch (error) {
                              console.error('❌ Erro ao baixar áudio:', error);
                              
                              // Mensagem específica para bloqueador de anúncios
                              if (error.message && error.message.includes('Failed to fetch')) {
                                toast.error('Bloqueador de anúncios detectado! Desative para baixar arquivos.');
                              } else {
                                toast.error(`Erro ao baixar áudio: ${error.message}`);
                              }
                            }
                          }}
                          className="p-2 text-blue-600 hover:bg-blue-200 rounded-lg transition-all"
                          title="Baixar áudio"
                        >
                          <Download className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Other Files (PDF, etc) */}
                  {work.files?.map((file, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="font-medium">{file.filename}</p>
                          <p className="text-sm text-gray-500">
                            {file.file_type} • {(file.file_size / 1024).toFixed(2)} KB
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            console.log('🔵 Iniciando download de arquivo:', file.filename);
                            const token = localStorage.getItem('token');
                            
                            const response = await fetch(
                              `${process.env.REACT_APP_BACKEND_URL}/api/works/${work.id}/files/${file.gridfs_id}`,
                              {
                                method: 'GET',
                                headers: {
                                  'Authorization': `Bearer ${token}`
                                }
                              }
                            );
                            
                            if (!response.ok) {
                              throw new Error(`Erro ${response.status}: ${response.statusText}`);
                            }
                            
                            console.log('🔵 Arquivo recebido, criando blob...');
                            const blob = await response.blob();
                            console.log('🔵 Blob criado, tamanho:', blob.size, 'bytes');
                            
                            const blobUrl = URL.createObjectURL(blob);
                            
                            const a = document.createElement('a');
                            a.href = blobUrl;
                            a.download = file.filename || `arquivo-${Date.now()}`;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            
                            setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
                            
                            toast.success('Arquivo baixado com sucesso!');
                            console.log('✅ Download concluído');
                          } catch (error) {
                            console.error('❌ Erro ao baixar arquivo:', error);
                            
                            // Mensagem específica para bloqueador
                            if (error.message && error.message.includes('Failed to fetch')) {
                              toast.error('Bloqueador de anúncios detectado! Desative para baixar arquivos.');
                            } else {
                              toast.error(`Erro ao baixar: ${error.message}`);
                            }
                          }
                        }}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                      >
                        <Download className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Lyrics */}
            {work.lyrics && (
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Letra</h2>
                <div className="bg-gray-50 p-4 rounded-lg whitespace-pre-wrap text-gray-700">
                  {work.lyrics}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Certificate */}
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl p-6 shadow-lg text-white sticky top-8">
              <div className="flex items-center gap-3 mb-4">
                <Award className="w-8 h-8" />
                <h2 className="text-xl font-bold">Certificado</h2>
              </div>

              {hasCertificate ? (
                <div className="space-y-4">
                  <div className="bg-white/20 rounded-lg p-4 backdrop-blur-sm">
                    <p className="text-sm opacity-90 mb-2">Hash do Certificado</p>
                    <p className="font-mono text-xs break-all">{work.certificate_hash}</p>
                  </div>

                  {/* Certificate Info Box */}
                  <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm border border-white/30">
                    <p className="text-xs leading-relaxed">
                      <strong>📄 Certificado Completo:</strong> Ao baixar, você receberá um documento único com:
                    </p>
                    <ul className="text-xs mt-2 space-y-1 ml-4">
                      <li>✓ Certificado de Anterioridade e Autenticidade</li>
                      <li>✓ QR Code de verificação</li>
                      {work.lyrics && <li>✓ Letra completa da música anexada</li>}
                    </ul>
                  </div>

                  <div className="grid grid-cols-1 gap-3">
                    <button
                      onClick={handleDownloadPDF}
                      disabled={downloadingPDF}
                      className="flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 hover:shadow-lg transition-all disabled:opacity-50"
                    >
                      {downloadingPDF ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Baixando Certificado...
                        </>
                      ) : (
                        <>
                          <Download className="w-6 h-6" />
                          Baixar Certificado em PDF
                        </>
                      )}
                    </button>
                    
                    {/* Botão de Download da Procuração (se BN registration foi solicitado) */}
                    {work.request_bn_registration && (
                      <button
                        onClick={handleDownloadPowerOfAttorney}
                        disabled={downloadingPOA}
                        className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-4 px-6 rounded-xl font-bold hover:from-green-600 hover:to-emerald-700 transition-all flex items-center justify-center gap-3 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {downloadingPOA ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Baixando Procuração...
                          </>
                        ) : (
                          <>
                            <FileText className="w-6 h-6" />
                            Baixar Procuração BN em PDF
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  
                  <p className="text-xs text-center text-white/70">
                    📄 O certificado será baixado automaticamente na sua pasta Downloads
                  </p>
                  
                  {work.request_bn_registration && (
                    <p className="text-xs text-center text-green-200 font-semibold">
                      🏛️ Procuração incluída para registro na Biblioteca Nacional
                    </p>
                  )}
                  
                  <p className="text-xs text-center text-white/70 mt-2">
                    ⚠️ Caso não baixe automaticamente, desative bloqueadores de anúncios/rastreadores temporariamente
                  </p>

                  <a
                    href={`/verify/${work.certificate_hash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full text-center py-2 text-sm hover:underline opacity-90"
                  >
                    Verificar certificado publicamente →
                  </a>
                </div>
              ) : (
                <div className="space-y-4">
                  {hasFiles ? (
                    <>
                      <p className="text-sm opacity-90">
                        Gere um certificado digital para sua obra e proteja seus direitos autorais.
                      </p>
                      <button
                        onClick={handleGenerateCertificate}
                        disabled={generatingCertificate}
                        className="w-full flex items-center justify-center gap-2 bg-white text-blue-600 py-3 rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50"
                      >
                        {generatingCertificate ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Gerando...
                          </>
                        ) : (
                          <>
                            <Award className="w-5 h-5" />
                            Gerar Certificado
                          </>
                        )}
                      </button>
                    </>
                  ) : (
                    <div className="bg-white/20 rounded-lg p-4 backdrop-blur-sm">
                      <Upload className="w-8 h-8 mx-auto mb-2 opacity-75" />
                      <p className="text-sm text-center opacity-90">
                        Você precisa fazer upload de pelo menos um arquivo antes de gerar o certificado.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default WorkDetailPage;
